import {
    getPromotionTool,
    PromotionTool,
    PromotionToolEnum,
} from '@/components/images/base64/PromotionTool';
import { ScreenContext } from '@/contexts/ScreenContext';
import { useAspectRatio } from '@/hooks/useAspectRatio';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { Dispatch, memo, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { AudioPlayContext } from '../../../../contexts/AudioPlayContext';
import { CdnContext } from '../../../../contexts/CdnContext';
import { getLocaleByCode } from '../../../../models/Locale';
import { POPUP_CONFIRM_ACTION } from '../../../../models/Popup';
import i18n from '../../../locales/i18n';
import { promotionToolSliceActions } from '../../../main/slice/promotiontool/promotionTool';
import { PromotionDetail } from '../../../setting/components/promotionDetail';
import { popupSliceActions } from '../../slice';
import { checkPromotionUrl } from '../PromotionUtil';
import './index.scss';
import { PromotionGacha } from './PromotionGacha';
import { PromotionRanking } from './PromotionRanking';
import { PromotionRecord } from './PromotionRecord';
import { PromotionSplash } from './PromotionSplash';
import { getPromotionToolState } from '@/modules/main/selector';
import { RootState } from '@/store/store';
import { usePromotionTool } from '@/hooks/popup/promotiontool/usePromotionTool';
import Button from '@mui/material/Button';

export enum PromotionEventType {
    Unknown,
    Promotion,
    Leaderboard,
}

export enum PromotionPage {
    Splash,
    Gacha,
    Ranking,
    Record,
}

type PromotionCurrentPageProps = {
    page: PromotionPage;
};
const PromotionViewPage = (props: PromotionCurrentPageProps) => {
    switch (props.page) {
        case PromotionPage.Splash:
            return <PromotionSplash />;
        case PromotionPage.Gacha:
            return <PromotionGacha />;
        case PromotionPage.Ranking:
            return <PromotionRanking />;
        case PromotionPage.Record:
            return <PromotionRecord />;
        default:
            return <></>;
    }
};
const areEqualsPage = (
    prev: PromotionCurrentPageProps,
    next: PromotionCurrentPageProps
) => {
    return prev.page === next.page;
};
const ViewPage = memo(PromotionViewPage, areEqualsPage);

type BannerListProps = {
    portrait: string | undefined;
    landscape: string | undefined;
};

export const PromotionDialog = () => {
    const [initialized, setInitialized] = useState<boolean>(false);
    const { isLandscape } = useContext(ScreenContext);
    const [screenRatio, setScreenRatio] = useState<number>(1);
    const { innerWidth, innerHeight } = useAspectRatio();
    const { playButtonAudio } = useContext(AudioPlayContext);
    const dispatch: Dispatch<AnyAction> = useDispatch();
    const { t } = useTranslation();
    const [bannerList, setBannerList] = useState<BannerListProps>();
    const [banner, setBanner] = useState<string | undefined>(undefined);

    const {
        Popup,
        Page,
        IsLoading,
        Id,
        IsOpenDialog,
        MoreDetailUrl,
        Language: PromotionLanguage,
    } = useSelector((state: RootState) => getPromotionToolState(state));

    const handleClose = () => {
        playButtonAudio();
        dispatch(promotionToolSliceActions.isOpenDialog(false));
    };
    const { isLeaderboard } = usePromotionTool();

    const preloadBackground = !isLeaderboard()
        ? 'linear-gradient(180deg, #062021 0%, #063734 54.5%, #0E4C4B 88%, #0F5150 100%)'
        : 'radial-gradient(146% 67.09% at 50% 50%, #55452B 0%, #131313 100%);';

    //On/Off promotion dialog control and page routing
    useEffect(() => {
        if (!IsOpenDialog) return;
        const permanentPage = isLeaderboard()
            ? PromotionPage.Ranking
            : PromotionPage.Gacha;
        let direction = Popup ? PromotionPage.Splash : permanentPage;
        if (!initialized) {
            setInitialized(true);
        } else {
            direction = permanentPage;
        }
        dispatch(promotionToolSliceActions.updatePage(direction));
    }, [IsOpenDialog]);

    useEffect(() => {
        if (!isLandscape) {
            if (innerWidth != 540 || innerHeight != 960) {
                setScreenRatio(Math.min(innerWidth / 540, innerHeight / 960));
            } else {
                setScreenRatio(1);
            }
        } else {
            if (innerWidth != 1920 || innerHeight != 1080) {
                setScreenRatio(Math.min(innerWidth / 1920, innerHeight / 1080));
            } else {
                setScreenRatio(1);
            }
        }
    }, [isLandscape, innerWidth, innerHeight]);

    const { promotionCdn } = useContext(CdnContext);
    useEffect(() => {
        if (IsLoading)
            dispatch(
                popupSliceActions.triggerConfirm(POPUP_CONFIRM_ACTION.NONE)
            );
    }, [IsLoading]);

    useEffect(() => {
        if (Page == PromotionPage.Splash) {
            if (!promotionCdn || Id <= 0) return;
            const bannerURL = checkPromotionUrl(promotionCdn, Id, 'JSON');
            if (bannerURL) {
                const currentCode = i18n.language;
                const curLang = getLocaleByCode('long', currentCode).map_code;
                fetch(bannerURL).then(response => {
                    if (response.status === 200) {
                        response.json().then(data => {
                            let langExist = Object.hasOwnProperty.call(
                                data,
                                curLang
                            );
                            const languageKey = langExist ? curLang : 'default';
                            if (langExist)
                                dispatch(
                                    promotionToolSliceActions.updateEventLanguage(
                                        curLang
                                    )
                                );
                            if (data[languageKey]) {
                                const tempList: BannerListProps = {
                                    portrait: undefined,
                                    landscape: undefined,
                                };
                                if (data[languageKey]['mobile'])
                                    tempList.portrait = `url('${promotionCdn + data[languageKey]['mobile']}')`;
                                if (data[languageKey]['desktop'])
                                    tempList.landscape = `url('${promotionCdn + data[languageKey]['desktop']}')`;
                                setBannerList(tempList);
                            }
                        });
                    }
                });
            }
        } else setBanner(preloadBackground);
    }, [Page, promotionCdn, Id]);

    useEffect(() => {
        if (Page !== PromotionPage.Splash) return;
        const banner = isLandscape
            ? bannerList?.landscape
            : bannerList?.portrait;
        if (banner) setBanner(banner);

        // if (!isLeaderboard()) {
        //     const promotionBanner = bannerList?.portrait;
        //     if (promotionBanner) setBanner(promotionBanner);
        // } else {
        //     const leaderboardBanner = isLandscape
        //         ? bannerList?.landscape
        //         : bannerList?.portrait;
        //     if (leaderboardBanner) setBanner(leaderboardBanner);
        // }
    }, [Page, bannerList, isLandscape]);

    useEffect(() => {
        dispatch(promotionToolSliceActions.isOpenDialog(Popup > 0 && Id > 0));
    }, [Popup, Id]);

    const [openMoreDetail, setOpenMoreDetail] = useState<boolean>(false);
    const [moreDetailUrl, setMoreDetailUrl] = useState<string>('');
    const handleMoreDetail = () => {
        playButtonAudio();
        let url = MoreDetailUrl;
        let replacement = url.indexOf('{lang}') >= 0;
        if (replacement) url = url.replace('{lang}', `_${PromotionLanguage}`);
        // else url += `_${PromotionLanguage}`;

        // if (os.name?.toLowerCase() === 'ios' && webView) {
        //     url += '&parenturl=' + encodeURIComponent(window.location.href);
        // }

        setMoreDetailUrl(url);
        setOpenMoreDetail(true);
    };

    return (
        <Dialog
            // container={() => document.getElementsByClassName('container')[0]}
            fullScreen
            open={IsOpenDialog}
            //onClose={}
            hideBackdrop={true}
            disableEscapeKeyDown={true}
            PaperProps={{
                sx: {
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    boxShadow: 'none',
                    // zIndex: theme.zIndex.drawer + 100,
                },
            }}
        >
            <DialogContent
                sx={{
                    // overflowY: 'visible',
                    padding: '0px',
                }}
            >
                <Box
                    className="event-container"
                    sx={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        padding: '0',
                        margin: '0',
                        transform: `translate(-50%, -50%) scale(${screenRatio})`,
                    }}
                >
                    <Button
                        className="btn-close"
                        onClick={handleClose}
                        sx={{ zIndex: 1 }}
                    >
                        <CloseIcon
                            sx={{
                                width: isLandscape ? 40 : 30,
                                height: isLandscape ? 40 : 30,
                            }}
                        />
                    </Button>
                    <Box
                        className={`panel ${isLeaderboard() ? 'leaderboard' : ''}`}
                    >
                        <div className="page-container">
                            {Page === PromotionPage.Splash && (
                                <Box
                                    className={`page-background ${isLeaderboard() ? 'leaderboard' : ''}`}
                                    sx={{
                                        background: `url(${PromotionTool[PromotionToolEnum.event_splash_preload_bg]})`,
                                    }}
                                />
                            )}
                            {banner && (
                                <Box
                                    className={`page-background ${isLeaderboard() ? 'leaderboard' : ''}`}
                                    sx={{
                                        background: banner,
                                    }}
                                />
                            )}
                        </div>
                        <ViewPage page={Page} />
                        {MoreDetailUrl !== '' && (
                            <Button
                                className="more-details"
                                onClick={handleMoreDetail}
                            >
                                <Box display={'flex'}>
                                    {getPromotionTool(
                                        PromotionToolEnum.event_ic_details
                                    )}
                                </Box>
                                {/* <BaseImage
                                        className="promotion_tool event_ic_details"
                                        scale={1}
                                    /> */}
                                <Typography
                                    sx={{
                                        marginLeft: '2px',
                                        color: '#DCDCDC',
                                        textDecoration: 'underline',
                                    }}
                                >
                                    {t('promotion_tool.details')}
                                </Typography>
                            </Button>
                        )}
                    </Box>
                </Box>
                {openMoreDetail && (
                    <Box
                        sx={{
                            backgroundColor: 'rgba(0,0,0,0.85)',
                            position: 'absolute',
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: '100%',
                            top: '0px',
                        }}
                    >
                        <PromotionDetail
                            setIsOpen={setOpenMoreDetail}
                            url={moreDetailUrl}
                            playButtonAudio={playButtonAudio}
                        />
                    </Box>
                )}
                {IsLoading ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '0',
                            left: '0',
                            backgroundColor: 'rgba(0, 0, 0, 0.45)',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <CircularProgress
                            size="6rem"
                            sx={{ color: '#98805d' }}
                        />
                    </Box>
                ) : (
                    <></>
                )}
            </DialogContent>
        </Dialog>
    );
};
